import { Literal, Union } from "runtypes";

import { assertNever } from "../errors.js";

import { isHistogram, isPie } from "./exploreChartUtils.js";
import { getFieldActiveScaleType } from "./exploreFieldUtils.js";
import {
  ExploreChartConfig,
  ExploreField,
  ExploreVisualizationType,
} from "./types.js";

import { CHART_FOLD_KEYS } from "./index.js";

export type ExploreFieldMenuItem =
  | "aggregation"
  | "truncUnit"
  | "sort"
  | "sortAllRows"
  | "measureSortIndicator"
  | "displayFormat"
  | "scaleType"
  | "filter"
  | "menuDivider"
  | "weekStart"
  | "editCalc"
  | "deleteCalc"
  | "editAsFormula"
  | "facet"
  | "lump"
  | "normalize"
  | "bin"
  | "binType"
  | "label";

export function getExploreFieldMenuItems(
  field: ExploreField | undefined,
  vizType: ExploreVisualizationType,
  chartConfig: ExploreChartConfig,
  hasCustomTimeUnits: boolean,
  isCalc: boolean,
  pivotTotalsEnabled = false,
  pivotSubtotalsEnabled = false,
): ExploreFieldMenuItem[] {
  const configFields: ExploreFieldMenuItem[] = [];
  if (!field) {
    return configFields;
  }
  const activeScaleType = getFieldActiveScaleType(field);

  const showCustomTimeUnits =
    ["DATE", "TIMESTAMPTZ", "TIMESTAMP"].includes(field.dataType) &&
    hasCustomTimeUnits;

  // Can always edit or delete calc columns
  isCalc && configFields.push("editCalc");
  isCalc && configFields.push("deleteCalc");
  if (vizType === "pivot-table") {
    if (
      field.fieldType !== "MEASURE" &&
      AggregableChannel.guard(field.channel)
    ) {
      configFields.push("aggregation");
      if (!isCalc) {
        configFields.push("editAsFormula");
      }
    }
    if (
      TruncationChannel.guard(field.channel) &&
      (activeScaleType === "datetime" || showCustomTimeUnits)
    ) {
      configFields.push("truncUnit");
      if (field.truncUnit === "week") {
        configFields.push("weekStart");
      }
    }

    if (field.channel === "value" && pivotSubtotalsEnabled) {
      configFields.push("sortAllRows");
    }
    if (
      DisplayFormatChannel.guard(field.channel) &&
      activeScaleType !== "string"
    ) {
      configFields.push("displayFormat");
    }
    if (
      pivotTotalsEnabled &&
      SortChannel.guard(field.channel) &&
      field.fieldType !== "MEASURE" &&
      field.aggregation == null
    ) {
      if (configFields.length > 0) {
        configFields.push("menuDivider");
      }
      // Add measure sort indicator if this field is sorted by a measure
      if (
        pivotSubtotalsEnabled &&
        (field.sort?.mode === "measure-ascending" ||
          field.sort?.mode === "measure-descending") &&
        field.sort?.measureOrder?.measureId
      ) {
        configFields.push("measureSortIndicator");
      }
      configFields.push("sort");
    }

    if (LumpChannel.guard(field.channel) && activeScaleType === "string") {
      configFields.push("lump");
    }
  } else if (vizType === "chart") {
    if (
      field.fieldType !== "MEASURE" &&
      AggregableChannel.guard(field.channel) &&
      field.value !== CHART_FOLD_KEYS &&
      !(isPie(chartConfig) && field.channel === "color")
    ) {
      configFields.push("aggregation");
      if (!isCalc) {
        configFields.push("editAsFormula");
      }
    }

    if (
      TruncationChannel.guard(field.channel) &&
      ((activeScaleType === "datetime" &&
        !(
          isPie(chartConfig) && ["color", "cross-axis"].includes(field.channel)
        )) ||
        showCustomTimeUnits) &&
      !(isHistogram(chartConfig) && field.channel === "base-axis")
    ) {
      configFields.push("truncUnit");
      if (field.truncUnit === "week") {
        configFields.push("weekStart");
      }
    }
    if (
      DisplayFormatChannel.guard(field.channel) &&
      activeScaleType !== "string"
    ) {
      configFields.push("displayFormat");
    }
    if (
      ScaleTypeChannel.guard(field.channel) &&
      field.fieldType !== "MEASURE" &&
      field.value !== CHART_FOLD_KEYS &&
      field.aggregation == null &&
      !(
        isPie(chartConfig) && ["color", "cross-axis"].includes(field.channel)
      ) &&
      !(isHistogram(chartConfig) && field.channel === "base-axis")
    ) {
      configFields.push("scaleType");
    }
    if (
      SortChannel.guard(field.channel) &&
      activeScaleType === "string" &&
      field.fieldType !== "MEASURE" &&
      field.aggregation == null
    ) {
      if (configFields.length > 0) {
        configFields.push("menuDivider");
      }
      configFields.push("sort");
    }

    if (
      LumpChannel.guard(field.channel) &&
      activeScaleType === "string" &&
      field.value !== CHART_FOLD_KEYS
    ) {
      configFields.push("lump");
    }
  } else {
    assertNever(vizType, vizType);
  }

  if (field.value !== CHART_FOLD_KEYS) {
    if (configFields.length > 0 && !configFields.includes("lump")) {
      configFields.push("menuDivider");
    }
    configFields.push("filter");
  }

  if (field.channel === "h-facet" || field.channel === "v-facet") {
    if (configFields.length > 0) {
      configFields.push("menuDivider");
    }
    configFields.push("facet");
    configFields.push("bin");
  }

  if (isHistogram(chartConfig) && field.channel === "base-axis") {
    if (configFields.length > 0) {
      configFields.push("menuDivider");
    }
    configFields.push("normalize");
    configFields.push("binType");
    configFields.push("bin");
  }

  configFields.push("label");
  return configFields;
}

export const TruncationChannel = Union(
  Literal("row"),
  Literal("column"),
  Literal("base-axis"),
  Literal("h-facet"),
  Literal("v-facet"),
  // "color", // add later
  // "tooltip" // add later
);

export const ScaleTypeChannel = Union(
  Literal("base-axis"),
  Literal("cross-axis"),
  Literal("color"),
);

export const SortChannel = Union(
  Literal("base-axis"),
  Literal("h-facet"),
  Literal("v-facet"),
  Literal("color"),
  Literal("row"),
  Literal("column"),
);

export const DisplayFormatChannel = Union(
  Literal("value"),
  Literal("row"),
  Literal("column"),
  Literal("base-axis"),
  Literal("cross-axis"),
  Literal("color"),
  Literal("tooltip"),
);

export const LumpChannel = Union(
  Literal("base-axis"),
  Literal("color"),
  Literal("h-facet"),
  Literal("v-facet"),
  Literal("row"),
  Literal("column"),
);

export const AggregableChannel = Union(
  Literal("value"),
  Literal("cross-axis"),
  Literal("color"),
  Literal("tooltip"),
);

export const AggregationRequiredChannel = Union(Literal("value"));

export const FoldKeysChannel = Union(
  Literal("base-axis"),
  Literal("color"),
  Literal("h-facet"),
  Literal("v-facet"),
  Literal("tooltip"),
);

export const BinChannel = Union(
  Literal("base-axis"),
  Literal("h-facet"),
  Literal("v-facet"),
);
